import { Link, useNavigate } from "react-router-dom";
import { ImagePlus, Clapperboard, Wand2, Maximize2, FolderPlus, ArrowRight, Plug } from "lucide-react";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import WorkspaceBanners from "@/components/common/WorkspaceBanners";
import { DemoBadge, EmptyState, StatusPill } from "@/components/common/States";
import ProjectCover from "@/components/common/ProjectCover";
import { useStudio, useLookups } from "@/store/StudioContext";
import { t } from "@/i18n";

const ToolTile = ({ to, icon: Icon, title, desc, id, primary, onClick, delay }) => {
  const cls = `group relative flex min-h-[110px] sm:min-h-[125px] flex-col justify-between overflow-hidden rounded-xl border p-4 sm:p-5 text-left transition-[transform,border-color,background-color] duration-200 active:scale-[0.99] hover:-translate-y-0.5 v-fade-up ${primary ? "border-[#f5b544]/60 bg-[#f5b544] text-[#1a1206] hover:bg-[#ffc45a]" : "border-[#303030] bg-[#1b1b1b] hover:border-[#4a4a4a]"}`;
  const inner = (
    <>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${primary ? "bg-[#1a1206]/10" : "border border-[#303030] bg-[#151515] text-[#f5b544]"}`}><Icon size={18} /></div>
      <div className="mt-4 sm:mt-6">
        <div className="font-display text-base sm:text-lg font-semibold">{title}</div>
        <p className={`mt-1 text-xs sm:text-[13px] leading-relaxed ${primary ? "text-[#1a1206]/75" : "text-[#a3a3a3]"}`}>{desc}</p>
      </div>
      <ArrowRight size={16} className={`absolute right-4 top-4 sm:right-5 sm:top-5 transition-transform group-hover:translate-x-0.5 ${primary ? "text-[#1a1206]/60" : "text-[#6b6b6b]"}`} />
    </>
  );
  const style = { animationDelay: `${delay}ms` };
  return onClick ? (
    <button data-testid={`home-tile-${id}`} onClick={onClick} className={cls} style={style}>{inner}</button>
  ) : (
    <Link data-testid={`home-tile-${id}`} to={to} className={cls} style={style}>{inner}</Link>
  );
};

const HomePage = () => {
  const { state, dispatch } = useStudio();
  const { assetById } = useLookups();
  const navigate = useNavigate();
  const recent = [...state.projects].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 4);
  const jobs = state.jobs.slice(0, 5);
  const budget = state.onboarding.budget;
  const connectionsCount = state.connections.length;

  const TOOLS = [
    { id: "image", to: "/tools/image", icon: ImagePlus, title: t.home.createImage, desc: t.home.createImageDesc },
    { id: "video", to: "/tools/video", icon: Clapperboard, title: t.home.createVideo, desc: t.home.createVideoDesc },
    { id: "edit", to: "/tools/edit", icon: Wand2, title: t.home.editImage, desc: t.home.editImageDesc },
    { id: "upscale", to: "/tools/upscale", icon: Maximize2, title: t.home.upscaler, desc: t.home.upscalerDesc },
  ];

  const createProject = () => {
    const id = `prj_${Date.now().toString(36)}`;
    dispatch({ type: "PROJECT_CREATE", payload: { id, name: t.common.untitled, format: state.onboarding.workspacePrefs?.format || "16:9" } });
    navigate(`/projects/${id}/storyboard`);
  };

  return (
    <Page testId="home-page">
      <PageHeader eyebrow={t.home.greeting(state.onboarding.workspaceName)} title={t.home.title} subtitle={t.banners.localOnly} />
      <WorkspaceBanners />

      <section className="mb-8 sm:mb-12">
        <div className="v-eyebrow mb-3">{t.home.quick}</div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:gap-3 lg:grid-cols-5">
          {TOOLS.map((tool, i) => <ToolTile key={tool.id} {...tool} delay={i * 50} />)}
          <ToolTile id="new-production" icon={FolderPlus} title={t.home.newProduction} desc={t.home.newProductionDesc} primary onClick={createProject} delay={200} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div className="v-eyebrow">{t.home.recent}</div>
            <Link to="/projects" data-testid="home-view-projects" className="text-xs text-[#a3a3a3] hover:text-[#f4f1ec]">{t.home.viewAll} →</Link>
          </div>
          {recent.length === 0 ? (
            <EmptyState title={t.projects.empty} body={t.home.recentEmpty} testId="home-projects-empty" action={<button className="v-btn-primary v-btn-sm" data-testid="home-empty-create-project" onClick={createProject}>{t.home.newProduction}</button>} />
          ) : (
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {recent.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}/storyboard`} data-testid={`home-project-${p.id}`} className="group v-panel overflow-hidden transition-[border-color] hover:border-[#4a4a4a]">
                  <ProjectCover project={p} state={state} assetById={assetById} fit="contain" className="aspect-[16/9] rounded-none border-0 bg-[#121212]" />
                  <div className="flex items-start justify-between gap-2 p-3.5 sm:p-4">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{p.name}</div>
                      <div className="mt-0.5 text-xs text-[#a3a3a3]">{t.projects.scenesCount(state.scenes.filter((s) => s.projectId === p.id).length)} · {t.formats[p.format]}</div>
                    </div>
                    {p.isDemo && <DemoBadge />}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <aside className="flex flex-col gap-4 sm:gap-6">
          <div className="v-panel p-3.5 sm:p-4">
            <div className="v-eyebrow mb-3">{t.home.connections}</div>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center justify-between" data-testid="home-connections-status"><span className="text-[#d8d4cd]">{t.home.imageVideo}</span><span className="flex items-center gap-1.5 text-xs text-[#a3a3a3]"><Plug size={12} />{connectionsCount > 0 ? t.connections.shortCount(connectionsCount) : t.common.notConfigured}</span></div>
              <div className="flex items-center justify-between" data-testid="home-provider-text"><span className="text-[#d8d4cd]">{t.home.textAssistant}</span><span className="flex items-center gap-1.5 text-xs text-[#a3a3a3]"><Plug size={12} />{t.common.notConnected}</span></div>
            </div>
            <div className="mt-4 border-t border-[#303030] pt-3">
              <div className="v-eyebrow mb-1">{t.home.budget}</div>
              <div data-testid="home-budget" className="text-sm">{budget.monthlyLimit ? t.home.budgetSummary(budget.monthlyLimit, budget.alertPct) : t.home.budgetNoLimit}</div>
              <div className="mt-1 text-xs text-[#a3a3a3]">{t.home.spent}: <span className="font-mono">{t.home.spentNone}</span></div>
            </div>
          </div>

          <div className="v-panel p-3.5 sm:p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="v-eyebrow">{t.home.tasks}</div>
              <Link to="/tasks" data-testid="home-view-tasks" className="text-xs text-[#a3a3a3] hover:text-[#f4f1ec]">{t.home.viewAll} →</Link>
            </div>
            {jobs.length === 0 ? (
              <p className="text-sm text-[#a3a3a3]" data-testid="home-tasks-empty">{t.home.tasksEmpty}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {jobs.map((j) => (
                  <li key={j.id} data-testid={`home-task-${j.id}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-[#d8d4cd]">{t.tasks.kinds[j.kind]} · <span className="text-[#a3a3a3]">{j.prompt?.slice(0, 32) || "—"}</span></span>
                    <StatusPill status={j.status} map={t.tasks.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </Page>
  );
};

export default HomePage;
